import { Platform } from 'react-native';
import Theme, { ThemeColor } from '@qnpm/q-theme';
import { commonUtil, dateUtil, deviceUtil, performanceUtil } from '@qnpm/q-design-utils';
import { BOTTOM_OTHER_INFO_TEXT, CALENDAR_TYPE, SCREEN_WIDTH, SELECTED_DATE_NUM } from './constant';
import { generatorRangeCalendar, getDefaultDateInfo, parseDateInfo, computeDateStr } from './dateUtil';
const { qunar } = Theme.color.palette;
const { calendar: calendarLayout } = Theme.layout;
const isIOS = Platform.OS === 'ios';
// eslint-disable-next-line complexity
export function getIDayStyle(props, dayWidth) {
    var _a, _b, _c, _d, _e, _f;
    const { selected, disabled, data, computedStyle } = props;
    const { computedLayout } = computedStyle || {};
    const { wrapper: { paddingHorizontal }, dayItem: { radius, space, width: _dayWidth, height: dayHeight } } = computedLayout || {};
    const { included, colors, extra, config, columnID, isFirstSelected, isLastSelected, selectedBackgroundColor } = data || {};
    const defaultColor = { color: ThemeColor.text1 };
    // 第一列
    const isFirstCol = columnID === 0;
    // 最后一列
    const isLastCol = columnID === 6;
    // 第一列和最后一列的边距
    const bigSpace = paddingHorizontal + space;
    // 包裹容器样式
    const wrapper = {
        // 最开始和最后的元素各自加padding
        paddingRight: isLastCol ? paddingHorizontal : space,
        paddingLeft: isFirstCol ? paddingHorizontal : 0
    };
    // 填充背景色
    const fill = {
        backgroundColor: 'transparent',
        width: dayWidth + (isFirstCol || isLastCol ? bigSpace : space),
        height: dayHeight,
        right: isLastSelected ? space : 0,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        left: undefined
    };
    const container = {
        backgroundColor: 'transparent',
        borderRadius: 0
    };
    // 默认色值获取顺序：extra->config->colors->default；
    // 这里的extra的色值来着selectedPayload，config的色值来自dataSource，colors来自组件内部的设置
    const header = {
        color: findColor([
            { color: ((_a = extra === null || extra === void 0 ? void 0 : extra.header) === null || _a === void 0 ? void 0 : _a.color) || '' },
            { color: ((_b = config === null || config === void 0 ? void 0 : config.header) === null || _b === void 0 ? void 0 : _b.color) || '' },
            { color: (colors === null || colors === void 0 ? void 0 : colors.header) || '' },
            defaultColor
        ])
    };
    const content = {
        color: findColor([
            { color: ((_c = extra === null || extra === void 0 ? void 0 : extra.content) === null || _c === void 0 ? void 0 : _c.color) || '' },
            { color: ((_d = config === null || config === void 0 ? void 0 : config.content) === null || _d === void 0 ? void 0 : _d.color) || '' },
            { color: (colors === null || colors === void 0 ? void 0 : colors.content) || '' },
            defaultColor
        ])
    };
    const footer = {
        color: findColor([
            { color: ((_e = extra === null || extra === void 0 ? void 0 : extra.footer) === null || _e === void 0 ? void 0 : _e.color) || '' },
            { color: ((_f = config === null || config === void 0 ? void 0 : config.footer) === null || _f === void 0 ? void 0 : _f.color) || '' },
            { color: (colors === null || colors === void 0 ? void 0 : colors.footer) || '' },
            defaultColor
        ])
    };
    // 选中
    if (selected) {
        // 选中时外部容器样式设置
        fill.backgroundColor = selectedBackgroundColor;
        // 第一个选中的日期设置圆角
        if (isFirstSelected) {
            fill.borderTopLeftRadius = radius;
            fill.borderBottomLeftRadius = radius;
            // 第一个选中且是在第一列，调整填充背景
            if (isFirstCol) {
                // @ts-ignore
                fill.left = bigSpace;
            }
        }
        // 最后一个选中的日期设置圆角
        if (isLastSelected) {
            fill.borderTopRightRadius = radius;
            fill.borderBottomRightRadius = radius;
            // 最后一个选中且在最后一列，调整填充背景
            if (isLastCol) {
                fill.right = paddingHorizontal;
            }
        }
        // 内容容器样式设置
        container.backgroundColor = 'transparent';
        container.borderRadius = radius;
        // 内容样式设置
        header.color = ThemeColor.white;
        content.color = ThemeColor.white;
        footer.color = ThemeColor.white;
    }
    else if (included) {
        // 包含
        fill.backgroundColor = qunar.shade9;
    }
    // disabled情况下，覆盖所有属性
    if (disabled) {
        // 如果是disabled状态，则置灰
        fill.backgroundColor = 'transparent';
        container.backgroundColor = 'transparent';
        header.color = ThemeColor.disableText;
        content.color = ThemeColor.disableText;
        footer.color = ThemeColor.disableText;
    }
    return { wrapper, fill, container, header, content, footer };
}
/**
 * 找到第一个可用颜色
 * @param colors
 */
function findColor(colors) {
    const result = colors.find((item) => item.color);
    if (!result) {
        return '';
    }
    return result.color;
}
/**
 * 获取每列的文本颜色
 * @param columnID
 */
export function computeColumnColor(columnID) {
    if (columnID === 0 || columnID === 6) {
        return ThemeColor.price;
    }
    return ThemeColor.text1;
}
/**
 * 输出warning日志
 * @param args
 */
export function warningLog(...args) {
    // eslint-disable-next-line no-console
    console.warn(`[Q-Calendar ${dateUtil.dateFormat(new Date(), 'yyyy-MM-dd hh:mm:ss')}]:`, ...args);
}
/* ------- 校验props中的日期 start ------- */
/**
 * 日期参数校验
 *  1. 最大最小日期是否合法
 *  2. 最大日期是否大于等于最小日期
 *  3. 最小日期是否大于等于当前日期
 *  4. 最大日期是否大于等于当前日期
 *  5. 所有默认选中日期是否在可选日期范围内
 *  6. 所有默认选中日期的后面的日期是都大于等于前面的日期
 * @param props
 * @param showLog
 */
export function validAndAdjustProps(props, showLog = true) {
    try {
        // 首先校验日历的渲染范围 - minDate&maxDate
        let copyProps = validAndAdjustRenderRange(Object.assign({}, props), showLog);
        // 校验日历可选范围 - startTime&endTime
        copyProps = validAndAdjustSelectRange(copyProps, showLog);
        // 校验日历默认选中日期 - selectedDate
        copyProps = validAndAdjustSelectedDate(copyProps, showLog);
        // 校验可选/不可选日期集合 - allowDates&denyDates
        copyProps = validAndAdjustAllowDenyDate(copyProps);
        return copyProps;
    }
    catch (e) {
        warningLog('validAndAdjustProps error:', e);
        return props;
    }
}
/**
 * 校验日历的渲染范围 - minDate&maxDate
 * @param copyProps
 * @param showLog
 */
export function validAndAdjustRenderRange(copyProps, showLog = true) {
    try {
        // 最小日期无效，重置为当前日期
        if (!dateUtil.isValidDate(copyProps.minDate)) {
            showLog &&
                warningLog(`最小日期：${dateUtil.dateFormat(copyProps.minDate)}无效，重置为当前日期`);
            copyProps.minDate = new Date();
        }
        // 最大日期无效，重置为363天后
        if (!dateUtil.isValidDate(copyProps.maxDate)) {
            showLog &&
                warningLog(`最大日期：${dateUtil.dateFormat(copyProps.maxDate)}无效，重置为当前日期后363天`);
            copyProps.maxDate = dateUtil.appendDate(new Date(), { date: 363 }, { format: true });
        }
        // 校验日期选择范围 - 最大日期必须大于等于最小日期
        if (dateUtil.compareDateWithDay(copyProps.maxDate, copyProps.minDate)) {
            showLog &&
                warningLog(`最小日期：${dateUtil.dateFormat(copyProps.minDate)} 大于 最大日期：${dateUtil.dateFormat(copyProps.maxDate)}，直接进行交换`);
            [copyProps.minDate, copyProps.maxDate] = [
                copyProps.maxDate,
                copyProps.minDate
            ];
        }
        // 检查最小日期是否合法 - 最小可选日期必须大于等于当前日期
        if (dateUtil.compareDateWithDay(copyProps.minDate, copyProps.minValidDate || new Date())) {
            showLog &&
                warningLog(`最小日期：${dateUtil.dateFormat(copyProps.minDate)} 小于 当前日期：${dateUtil.dateFormat(new Date())}，最小日期设置为当前日期`);
            copyProps.minDate = new Date();
        }
        // 检查最大日期是否合法 - 最大可选日期必须大于等于当前日期
        if (dateUtil.compareDateWithDay(copyProps.maxDate, new Date())) {
            showLog &&
                warningLog(`最大日期：${dateUtil.dateFormat(copyProps.maxDate)} 小于 当前日期，重置为当前日期后363天`);
            copyProps.maxDate = dateUtil.appendDate(new Date(), { date: 363 }, { format: true });
        }
        return copyProps;
    }
    catch (e) {
        warningLog('validAndAdjustRenderRange error:', e);
        return copyProps;
    }
}
/**
 * 校验日历可选范围 - startTime&endTime
 * @param copyProps
 * @param showLog
 */
export function validAndAdjustSelectRange(copyProps, showLog = true) {
    try {
        // 根据最大最小日期算出渲染范围
        const renderRange = {
            // 渲染开始日期推到当前月份1号
            start: new Date(dateUtil.toDate(copyProps.minDate).setDate(1)),
            // 渲染结束日期推到当前月份最后一天
            end: new Date(dateUtil.appendDate(copyProps.maxDate, {
                month: 1
            }).setDate(0))
        };
        // 校验可选开始日期格式
        if (!dateUtil.isValidDate(copyProps.startTime)) {
            showLog &&
                warningLog(`可选开始日期：${dateUtil.dateFormat(copyProps.startTime)} 格式不正确，重置为最小日期：${dateUtil.dateFormat(copyProps.minDate)}`);
            copyProps.startTime = copyProps.minDate;
        }
        // 校验可选结束日期格式
        if (!dateUtil.isValidDate(copyProps.endTime)) {
            showLog &&
                warningLog(`可选结束日期：${dateUtil.dateFormat(copyProps.endTime)} 格式不正确，重置为最大日期：${dateUtil.dateFormat(copyProps.maxDate)}`);
            copyProps.endTime = copyProps.maxDate;
        }
        // 校验可选范围 - 可选结束日期不能小于可选开始日期 -> 确保结束大于开始
        if (dateUtil.compareDateWithDay(copyProps.endTime, copyProps.startTime)) {
            showLog &&
                warningLog(`可选结束日期：${dateUtil.dateFormat(copyProps.endTime)} 小于 可选开始日期：${dateUtil.dateFormat(copyProps.startTime)}，直接交换`);
            [copyProps.startTime, copyProps.endTime] = [
                copyProps.endTime,
                copyProps.startTime
            ];
        }
        // 校验可选范围 - 最小可选日期必须大于等于日历最小日期
        if (dateUtil.compareDateWithDay(copyProps.startTime, renderRange.start)) {
            showLog &&
                warningLog(`可选开始日期：${dateUtil.dateFormat(copyProps.startTime)} 小于 日历最小渲染日期：${dateUtil.dateFormat(renderRange.start)}，重置为最小日期`);
            copyProps.startTime = renderRange.start;
        }
        // 校验可选范围 - 最大可选日期必须小于等于日历最大日期
        if (dateUtil.compareDateWithDay(renderRange.end, copyProps.endTime)) {
            showLog &&
                warningLog(`可选结束日期：${dateUtil.dateFormat(copyProps.endTime)} 大于 日历最大渲染日期：${dateUtil.dateFormat(renderRange.end)}，重置为最大日期`);
            copyProps.endTime = renderRange.end;
        }
        return copyProps;
    }
    catch (e) {
        warningLog('validAndAdjustSelectRange error:', e);
        return copyProps;
    }
}
/**
 * 校验日历默认选中日期 - selectedDate
 * @param copyProps
 * @param showLog
 */
export function validAndAdjustSelectedDate(copyProps, showLog = true) {
    try {
        let { minDate, maxDate, selectedDate = [], mode = CALENDAR_TYPE.SINGLE } = copyProps;
        // 确定渲染范围
        const rangeParam = {
            start: minDate,
            end: new Date(dateUtil.appendDate(maxDate, {
                month: 1
            }).setDate(0))
        };
        // 防止外部传入不正确的选中日期
        selectedDate = selectedDate.slice(0, SELECTED_DATE_NUM[mode]);
        // 校验每一个默认选中日期，确保其在渲染范围内
        selectedDate.forEach((item, index) => {
            // 如果当前日期不在可选范围内，则进行调整
            if (!dateUtil.isWithinDayRange(item, rangeParam)) {
                const isFirst = index === 0;
                // 如果是第一个日期不合法，直接重置为第一个可选日期；如果为非第一个日期不合法，重置为前一个日期的后一天
                const baseDate = isFirst
                    ? rangeParam.start
                    : selectedDate[index - 1];
                selectedDate[index] = dateUtil.appendDate(baseDate, { date: isFirst ? 0 : 1 }, { format: true });
                showLog &&
                    warningLog(`第 ${index + 1} 个默认选中日期(${dateUtil.dateFormat(item)})不在日历渲染范围 ${dateUtil.dateFormat(rangeParam.start)}~${dateUtil.dateFormat(rangeParam.end)} 内，重置为${dateUtil.dateFormat(selectedDate[index])}确保正常渲染；请检查传入数据！！！`);
            }
        });
        // 校验后面的选中日期比前面的大 - 到这里时所有的选中日期都已经才可选范围了
        for (let i = 1; i < selectedDate.length; i++) {
            const prevDate = selectedDate[i - 1];
            const currentDate = selectedDate[i];
            // 如果后面的选中日期小于前面的选中日期，则需要调整
            if (dateUtil.compareDateWithDay(currentDate, prevDate)) {
                showLog &&
                    warningLog(`第 ${i + 1} 个选中日期(${dateUtil.dateFormat(currentDate)})小于第${i}个选中日期(${dateUtil.dateFormat(prevDate)})，重置为${dateUtil.dateFormat(selectedDate[i])}确保正常渲染，请检查传入参数！！！`);
                selectedDate[i] = dateUtil.appendDate(prevDate, { date: 1 }, { format: true });
            }
        }
        return Object.assign(Object.assign({}, copyProps), { selectedDate });
    }
    catch (e) {
        warningLog('validAndAdjustSelectedDate error', e);
        return copyProps;
    }
}
/**
 * 校验可选/不可选日期集合 - allowDates&denyDates
 * @param copyProps
 */
export function validAndAdjustAllowDenyDate(copyProps) {
    try {
        // 过滤可选日期集合中不合法的日期
        if (commonUtil.isValidArray(copyProps.allowDates)) {
            copyProps.allowDates = filterAndConvertDates(copyProps, 'allowDates');
        }
        // 过滤不可选日期集合中不合法的日期
        if (commonUtil.isValidArray(copyProps.denyDates)) {
            copyProps.denyDates = filterAndConvertDates(copyProps, 'denyDates');
        }
        return copyProps;
    }
    catch (e) {
        warningLog('validAndAdjustAllowDenyDate error', e);
        return copyProps;
    }
}
/* ------- 校验props中的日期 end ------- */
/**
 * 过滤数据中某个日期集合中的不合法日期 - 并将其转为yyyy-MM-dd类型日期字符串
 * @param data
 * @param propName
 */
function filterAndConvertDates(data, propName) {
    const dates = data[propName] || [];
    const result = [];
    // 遍历移除不合法的日期，并将合法的日期格式化为字符串类型
    dates.forEach((item, index) => {
        const isValid = dateUtil.isValidDate(item);
        if (isValid) {
            result.push(dateUtil.dateFormat(item));
        }
        else {
            warningLog(`属性 [${propName}] 的第 ${index + 1} 个日期(${item})不合法，已被移除。`);
        }
    });
    return result;
}
/**
 * 组装回调数据
 * @param selectedDate
 * @param dataSource
 * @param selectedIndex
 */
export function computeCallbackData(selectedDate, dataSource, selectedIndex = 0) {
    const getItem = (item, index) => {
        const { value, year, month, date, key, extra, ids } = item;
        const { footer: eFooter, header: eHeader } = extra || {};
        const { footer, header } = dataSource[value] || dataSource[key] || {};
        const headerText = (eHeader === null || eHeader === void 0 ? void 0 : eHeader.text) || (header === null || header === void 0 ? void 0 : header.text);
        const footerText = (eFooter === null || eFooter === void 0 ? void 0 : eFooter.text) || (footer === null || footer === void 0 ? void 0 : footer.text);
        return {
            value,
            year,
            month,
            date,
            footer: footerText,
            header: headerText,
            selectedIndex: index,
            week: ids === null || ids === void 0 ? void 0 : ids.columnID
        };
    };
    // 数组
    if (Array.isArray(selectedDate)) {
        // 过滤空的日期
        return selectedDate
            .filter((val) => val)
            .map((item, index) => getItem(item, index));
    }
    // 非数组
    return getItem(selectedDate, selectedIndex);
}
/**
 * 计算提示浮层位置
 * @param toolTipSize
 * @param position
 * @param needScale
 */
export function computeToolTipPosition(toolTipSize, position, needScale) {
    try {
        const { wrapper, dayItem, dayRow } = needScale
            ? calendarLayout.scaled
            : calendarLayout.unscaled;
        const { paddingHorizontal } = wrapper;
        const { width, height } = toolTipSize || {};
        const { rowID, columnID } = position || {};
        // tooltip底部距离日期的距离
        const tooltipBottomSpace = 1;
        // 垂直方向距离
        const pageY = rowID * (dayItem.height + dayRow.bottom) -
            height / 2 -
            dayItem.height / 2 -
            tooltipBottomSpace;
        let pageX = 0;
        let arrowPosition = 'center';
        // 选择了周一到周五时向下箭头在中间
        if (columnID >= 1 && columnID <= 5) {
            pageX =
                paddingHorizontal +
                    (dayItem.width + dayItem.space) * columnID -
                    width / 2 +
                    dayItem.width / 2;
            arrowPosition = 'center';
        }
        else if (columnID === 0) {
            pageX = paddingHorizontal;
            arrowPosition = 'left';
        }
        else if (columnID === 6) {
            pageX = SCREEN_WIDTH - paddingHorizontal - width;
            arrowPosition = 'right';
        }
        const arrowOffset = {};
        // 如果计算的X轴距离过小，则进行调整
        if (pageX < paddingHorizontal) {
            pageX = paddingHorizontal;
            arrowPosition = 'left';
            // 这里不需要再减箭头的宽度，left时再带自身宽度偏移
            arrowOffset.left =
                (columnID + 0.5) * dayItem.width + columnID * dayItem.space;
        }
        else if (SCREEN_WIDTH - width - paddingHorizontal < pageX) {
            const offset = 6 - columnID;
            // 如果计算的X轴偏移加提示宽度超出屏幕宽度时，进行调整
            pageX = SCREEN_WIDTH - paddingHorizontal - width;
            arrowPosition = 'right';
            arrowOffset.right =
                (offset + 0.5) * dayItem.width + offset * dayItem.space;
        }
        return { pageX, pageY, position: arrowPosition, arrowOffset };
    }
    catch (e) {
        warningLog('computeToolTipPosition error:', e);
        return { pageX: 0, pageY: 0, position: 'center', arrowOffset: {} };
    }
}
/* ------- 计算默认state start ------- */
export function computeInitState(props) {
    const { selectedDate = [], minDate, maxDate, startWeek = 0, blankOut = true, startWith, mode, selectedPayload = [], dataSource, serviceTime, getMergeDesc, extraDayContent, startTime, endTime, allowDates, denyDates } = props;
    const calendarData = generatorRangeCalendar({
        minDate: minDate,
        maxDate: maxDate,
        start: startWeek,
        blankOut,
        serviceTime,
        extraDayContent
    });
    // 如果默认日期为空，则直接返回
    if (!commonUtil.isValidArray(selectedDate)) {
        return {
            selectedDate: [],
            calendarData,
            dataSource,
            startTime,
            endTime,
            allowDates,
            denyDates
        };
    }
    let newSelected = [];
    selectedDate.forEach((item, index) => {
        // 只有在可用日期时，才转换
        if (dateUtil.isValidDate(item)) {
            const dateInfo = getDefaultDateInfo(item);
            const ids = findDateIndex(calendarData, dateInfo);
            const extraData = selectedPayload[index];
            newSelected.push(Object.assign(Object.assign({}, dateInfo), { ids, extra: extraData }));
            // 设置默认选中日期
            updateCalendarWithID(calendarData, ids, true, 'selected', extraData, index);
        }
    });
    // 指明选择方向时，如果默认日期数量不合法，则默认添加空数据
    newSelected = !startWith
        ? newSelected
        : checkAndFillSelectedDate(newSelected, startWith);
    // 只有非单选时，才需要检查
    if (mode !== CALENDAR_TYPE.SINGLE) {
        const { extra } = getSelectedExtra(newSelected, selectedPayload, getMergeDesc);
        const len = newSelected.length - 1;
        const { ids } = newSelected[len] || {};
        updateCalendarWithID(calendarData, ids, true, 'selected', extra, len);
    }
    const { year, month } = newSelected.slice(-1)[0] || {};
    return {
        selectedDate: newSelected,
        calendarData,
        dataSource,
        showToolTipMonth: computedMonthKey(year, month),
        startTime,
        endTime,
        allowDates,
        denyDates
    };
}
/**
 * 计算月份key值
 * @param year
 * @param month
 */
export function computedMonthKey(year, month) {
    const kMonth = month < 10 ? `0${month}` : month;
    return `${year}-${kMonth}-01`;
}
/**
 * 传了startWith时，校正默认选择数据
 * @param selectedDate
 * @param startWith
 */
function checkAndFillSelectedDate(selectedDate, startWith) {
    if (startWith === 'start') {
        return selectedDate;
    }
    const len = selectedDate.length;
    // 如果要开始选中间的，则第一个日期必须存在
    if (startWith === 'middle' && len === 0) {
        return [
            {
                value: '',
                zhStr: '',
                year: 0,
                month: 0,
                date: 0,
                showValue: 0,
                key: '',
                position: 'unknown'
            }
        ];
    }
    // 如果要选择最后一个日期，则前两个必须存在
    if (startWith === 'end' && len < 2) {
        const result = [...selectedDate];
        // 要填充的数量
        const count = 2 - len;
        for (let i = 0; i < count; i++) {
            result.push({
                value: '',
                zhStr: '',
                year: 0,
                month: 0,
                date: 0,
                showValue: 0,
                key: '',
                position: 'unknown'
            });
        }
        return result;
    }
    return selectedDate;
}
export function convertSectionCalendarData(calendarData) {
    return calendarData.map((item, index) => ({
        index,
        monthKey: item.monthKey,
        title: item.title,
        year: item.year,
        month: item.month,
        data: [item.weekList]
    }));
}
export function convertListViewSectionData(calendarData) {
    const result = {};
    calendarData.forEach((item, index) => {
        // 校正index
        item.index = index;
        result[item.title] = [item];
    });
    return result;
}
/**
 * 查找选中日期坐标
 * @param calendarData
 * @param data
 */
function findDateIndex(calendarData, data) {
    // 找到匹配的月份
    const blockID = calendarData.findIndex((blockRow) => data.year === blockRow.year && data.month === blockRow.month);
    // 如果不在日历范围内，则返回默认值
    if (blockID !== -1) {
        const blockRow = calendarData[blockID];
        let rowID = 0;
        let columnID = 0;
        // 在匹配的月份中查找
        for (const [_rowID, weekRow] of blockRow.weekList.entries()) {
            const _columnID = weekRow.findIndex((column) => column.date === data.date);
            // 如果匹配到了，则停止查找
            if (_columnID !== -1) {
                rowID = _rowID;
                columnID = _columnID;
                break;
            }
        }
        return { blockID, rowID, columnID };
    }
    return { blockID: 0, columnID: 0, rowID: 0 };
}
/* ------- 计算默认state end ------- */
/* ------- 处理日期更新逻辑 start ------- */
export function computeCalendarData({ calendarData, data, selectedDate, mode, startWith, position, selectedPayload, getMergeDesc, canSelectSameDay }) {
    // 设置日期位置
    data.ids = position;
    // 单选日期
    if (mode === CALENDAR_TYPE.SINGLE) {
        return selectedSingleCalendar(calendarData, position, selectedDate, data, selectedPayload);
    }
    // 范围选择
    if (mode === CALENDAR_TYPE.RANGE) {
        return selectedMultiCalendar({
            calendarData,
            data,
            position,
            selectedDate,
            startWith,
            selectedPayload,
            maxLen: 2,
            getMergeDesc,
            canSelectSameDay
        });
    }
    // 多选
    if (mode === CALENDAR_TYPE.MULTI) {
        return selectedMultiCalendar({
            calendarData,
            data,
            position,
            selectedDate,
            startWith,
            selectedPayload,
            maxLen: 3,
            getMergeDesc,
            canSelectSameDay
        });
    }
    return {
        calendarData,
        selectedDate,
        needCallback: true,
        sameDayResult: {},
        selectedIndex: 0
    };
}
/**
 * 计算初始限制信息
 * @param calendarData
 * @param position
 * @param resetData
 * @param payload
 */
export function computeLimitCalendarData(calendarData, position, resetData, payload) {
    const origin = [...calendarData];
    // 取消不合格的选择
    resetCalendarDate(origin, [resetData]);
    // 选中校正的日期
    updateCalendarWithID(origin, position, true, 'selected', payload, 1);
    return origin;
}
/**
 * 日期单选
 * @param origin
 * @param position
 * @param selectedDate
 * @param data
 * @param selectedPayload
 */
function selectedSingleCalendar(origin, position, selectedDate, data, selectedPayload = []) {
    // 取消已选择的日期
    resetCalendarDate(origin, selectedDate);
    // 设置新的日期的选中状态
    updateCalendarWithID(origin, position, true, 'selected', selectedPayload[0], 0);
    return {
        calendarData: origin,
        selectedDate: [data],
        needCallback: true,
        sameDayResult: {},
        selectedIndex: 0
    };
}
// 每个开始选择日期对应的下标
const startWithMap = {
    start: 0,
    middle: 1,
    end: 2
};
/**
 * 计算多选日期
 * @param calendarData
 * @param data
 * @param position
 * @param selectedDate
 * @param startWith
 * @param selectedPayload
 * @param maxLen
 * @param getMergeDesc
 * @param canSelectSameDay
 */
function selectedMultiCalendar({ calendarData, data, position, selectedDate, startWith, selectedPayload = [], maxLen = 1, getMergeDesc, canSelectSameDay }) {
    var _a, _b, _c;
    const maxIndex = maxLen - 1;
    let newSelectedDate = [...selectedDate];
    let ids;
    let needCallback = false;
    let selectedIndex;
    // 如果不存续选择同一天，直接变为重选操作
    if (!canSelectSameDay && hasSelected(selectedDate, data)) {
        // 重置已经选择的日期
        resetCalendarDate(calendarData, newSelectedDate);
        newSelectedDate = [data];
        selectedIndex = 0;
    }
    else {
        // 检查是否完成选择
        const checkFinishSelect = () => {
            // 如果没有指明选择方向且列表不为空，则一次进行选择
            const matchArr = newSelectedDate.filter((item) => item.ids);
            const matchLen = commonUtil.isValidArray(matchArr)
                ? matchArr.length
                : 0;
            // 是否多个日期都已选择
            const isFinished = matchLen === maxLen;
            if (isFinished) {
                // 重置已经选择的日期
                resetCalendarDate(calendarData, newSelectedDate);
                newSelectedDate = [data];
            }
            return matchLen;
        };
        // 如果指明了选择方向，则直接选择
        if (startWith) {
            const checkArr = newSelectedDate.slice(0, startWithMap[startWith]);
            const needReset = checkStartWithAction(checkArr, data);
            if (needReset) {
                needCallback = false;
                // 重置已经选择的日期
                resetCalendarDate(calendarData, newSelectedDate);
                newSelectedDate = [data];
                selectedIndex = 0;
            }
            else {
                needCallback = true;
                switch (startWith) {
                    case 'start':
                        ids = (_a = newSelectedDate[0]) === null || _a === void 0 ? void 0 : _a.ids;
                        newSelectedDate[0] = data;
                        selectedIndex = 0;
                        break;
                    case 'middle':
                        ids = (_b = newSelectedDate[1]) === null || _b === void 0 ? void 0 : _b.ids;
                        newSelectedDate[1] = data;
                        selectedIndex = 1;
                        break;
                    case 'end':
                        ids = (_c = newSelectedDate[maxIndex]) === null || _c === void 0 ? void 0 : _c.ids;
                        newSelectedDate[maxIndex] = data;
                        selectedIndex = maxIndex;
                        break;
                }
            }
            updateCalendarWithID(calendarData, ids, false, 'normal');
        }
        else {
            // 如果没有指明选择方向且已选择列表为空，则直接将选择的日期放入列表
            if (!commonUtil.isValidArray(newSelectedDate)) {
                newSelectedDate.push(data);
                selectedIndex = 0;
            }
            else {
                const matchLen = checkFinishSelect();
                // 如果没有完成选择，则进行设置
                if (matchLen < maxLen) {
                    const prevData = newSelectedDate[matchLen - 1];
                    // 这里的日期均存在，所以不会返回null
                    const prevDate = dateUtil.convertDate(prevData.value);
                    const nextDate = dateUtil.convertDate(data.value);
                    // 后选择的不大于先选择的，则重置
                    if (nextDate >= prevDate) {
                        newSelectedDate[matchLen] = data;
                        selectedIndex = matchLen;
                    }
                    else {
                        // 重置已经选择的日期
                        resetCalendarDate(calendarData, newSelectedDate);
                        newSelectedDate = [data];
                        selectedIndex = 0;
                    }
                }
                else {
                    selectedIndex = 0;
                }
                // 如果最后设置的是滴3个日期，则表示需要回调了
                needCallback = selectedIndex === maxIndex;
            }
        }
    }
    // 获取额外信息
    const { extra, mapInfo } = getSelectedExtra(newSelectedDate, selectedPayload, getMergeDesc);
    // 设置选中日期
    updateCalendarWithID(calendarData, position, true, 'selected', extra, selectedIndex);
    return {
        calendarData,
        selectedDate: newSelectedDate,
        needCallback,
        sameDayResult: mapInfo,
        selectedIndex
    };
}
/**
 * 指明选择方向时，校验每次选择的日期
 * @param selectedDate
 * @param data
 */
function checkStartWithAction(selectedDate, data) {
    // 如果是空数组，则不需要重置
    if (!commonUtil.isValidArray(selectedDate)) {
        return false;
    }
    // 查找已选择日期中比当前日期小的日期数量
    const invalidArr = selectedDate.filter((item) => dateUtil.compareDateWithDay(data.value, item.value));
    return invalidArr.length > 0;
}
/**
 * 重置已选择的日期
 * @param calendarData
 * @param newSelectedDate
 */
function resetCalendarDate(calendarData, newSelectedDate) {
    // 重置已经选择的日期
    newSelectedDate.forEach((item) => updateCalendarWithID(calendarData, item.ids, false, 'normal'));
}
/**
 *
 * @param newSelectedDate
 * @param selectedPayload
 * @param getMergeDesc
 */
function getSelectedExtra(newSelectedDate, selectedPayload = [], getMergeDesc) {
    // 计算是否有选择到相同天
    const { indexes, mapInfo } = computeSelectSameDay(newSelectedDate);
    // 找到相同天的下标
    const overLeapIndexes = Object.values(indexes).find((item) => item.length > 1);
    // 执行回调，获取展示文案
    const result = overLeapIndexes && getMergeDesc && commonUtil.isFunction(getMergeDesc)
        ? getMergeDesc(overLeapIndexes)
        : getDefaultMergeDesc(selectedPayload, overLeapIndexes);
    // 获取选中日期的展示数据
    let extra = getMultiExtra(newSelectedDate, selectedPayload);
    // 如果返回了展示文案，则更新
    if (result) {
        extra = Object.assign(Object.assign({}, extra), { showArrow: false, header: Object.assign(Object.assign({}, extra === null || extra === void 0 ? void 0 : extra.header), { text: result }) });
    }
    return { extra, mapInfo };
}
/**
 * 获取默认的展示文字
 * @param selectedPayload
 * @param indexes
 */
function getDefaultMergeDesc(selectedPayload = [], indexes) {
    const desc = [];
    indexes &&
        indexes.forEach((key) => {
            var _a;
            const { text } = ((_a = selectedPayload[key]) === null || _a === void 0 ? void 0 : _a.header) || {};
            if (text) {
                desc.push(text);
            }
        });
    return desc.join('/');
}
/**
 * 获取下一个提示信息
 * @param selectedDate
 * @param selectedPayload
 */
function getMultiExtra(selectedDate = [], selectedPayload = []) {
    const matchLen = selectedDate.length || 0;
    return selectedPayload[matchLen - 1] || {};
}
/**
 * 更新日期状态
 * @param origin
 * @param position
 * @param status
 * @param type
 * @param extra
 * @param index
 */
// eslint-disable-next-line max-params
function updateCalendarWithID(origin, position, status, type, extra, index) {
    try {
        if (!position || !commonUtil.isValidObject(position)) {
            return origin;
        }
        const { blockID, rowID, columnID } = position;
        origin[blockID].weekList[rowID][columnID].selected = status;
        origin[blockID].weekList[rowID][columnID].selectedType = type;
        origin[blockID].weekList[rowID][columnID].extra = extra;
        origin[blockID].weekList[rowID][columnID].selectedIndex = index;
        return origin;
    }
    catch (e) {
        warningLog('updateCalendarWithID error:', e);
        return origin;
    }
}
/**
 * 计算选中日期是否是有重复
 * @param selectedDate
 */
export function computeSelectSameDay(selectedDate = []) {
    try {
        if (!commonUtil.isValidArray(selectedDate)) {
            return { indexes: {}, mapInfo: {} };
        }
        const result = {
            indexes: {},
            mapInfo: {}
        };
        selectedDate.forEach((item, index) => {
            if (result.indexes[item.key]) {
                result.indexes[item.key].push(index);
            }
            else {
                result.indexes[item.key] = [index];
            }
            result.mapInfo[item.key] = result.mapInfo.hasOwnProperty(item.key);
        });
        return result;
    }
    catch (e) {
        warningLog('computeSelectSameDay error:', e);
        return { indexes: {}, mapInfo: {} };
    }
}
/**
 * 计算限制信息
 * @param calendarData
 * @param data
 * @param firstDate
 * @param secondDate
 */
export function validSelectedLimit(calendarData, data, firstDate, secondDate) {
    try {
        const { max, min } = data;
        const result = {};
        // 只有第二个日期大于第一个时才校验，否则直接返回
        if (!dateUtil.compareDateWithDay(firstDate.value, secondDate.value)) {
            return result;
        }
        // 计算间隔天数 - 加1是为了计算入住的夜晚时间
        const distanceDay = dateUtil.distanceTime(firstDate.value, secondDate.value);
        // 如果选择的两个日期间的天数小于最小限制，则返回提示信息
        if (min && distanceDay < min.day) {
            const info = getLimitDateInfo(calendarData, firstDate.value, min.day + 1);
            result.message = min.message;
            result.date = info.data;
            result.position = info.ids;
        }
        // 如果选择的两个日期间的天数大于最大限制，则返回提示信息
        if (max && distanceDay > max.day) {
            const info = getLimitDateInfo(calendarData, firstDate.value, max.day + 1);
            result.message = max.message;
            result.date = info.data;
            result.position = info.ids;
        }
        return result;
    }
    catch (e) {
        warningLog('validSelectedLimit error:', e);
        return {};
    }
}
/**
 * 获取提示信息
 * @param calendarData
 * @param prevValue
 * @param day
 */
function getLimitDateInfo(calendarData, prevValue, day) {
    // 不算当前天
    const nextDate = dateUtil.appendDate(prevValue, { date: day - 1 });
    const dateInfo = parseDateInfo(nextDate);
    const data = Object.assign(Object.assign({}, computeDateStr({
        year: dateInfo.year,
        month: dateInfo.month,
        date: dateInfo.date,
        blankOut: false
    })), { position: 'unknown' });
    return {
        data,
        ids: findDateIndex(calendarData, data)
    };
}
/* ------- 处理日期更新逻辑 end ------- */
/* ------- 计算滚动距离逻辑 start ------- */
/**
 * 计算当前选中月份的位置 - 用于滚动
 * @param positionIndexes
 * @param calendarLayout
 * @param appendHeader
 * @param scrollPosition
 */
export function computedInitScrollPosition(positionIndexes, calendarLayout, appendHeader, scrollPosition = 'month') {
    try {
        const { monthHeader, dayItem, dayRow } = calendarLayout || {};
        // 所有的月份下标
        const allIndexes = Object.keys(positionIndexes);
        // @ts-ignore 当前月份下标
        const currentIndex = Math.max(...allIndexes);
        // 选中的日期前有几个月
        const totalMonth = allIndexes.length - 1;
        // 每一个月份的头部高度固定
        const extraHeight = monthHeader.height +
            monthHeader.marginBottom +
            monthHeader.marginTop;
        let totalRow = 0;
        // 计算总的行数
        if (scrollPosition === 'month') {
            Object.keys(positionIndexes).forEach((sectionIndex) => {
                // 跳过当前月份的行数
                if (currentIndex !== parseInt(sectionIndex, 10)) {
                    totalRow += positionIndexes[sectionIndex];
                }
            });
        }
        else {
            Object.keys(positionIndexes).forEach((sectionIndex) => {
                totalRow += positionIndexes[sectionIndex];
            });
        }
        // 所有月份头部的高度
        const headerOffset = extraHeight * totalMonth;
        // 所有月份内容的高度
        const contentOffset = totalRow * (dayItem.height + dayRow.bottom);
        // 总的滚动高度
        const totalOffset = headerOffset + contentOffset;
        // 是否需要再额外加一个header的高度
        if (appendHeader) {
            return totalOffset + monthHeader.height;
        }
        return totalOffset;
    }
    catch (e) {
        warningLog('computedInitScrollPosition error:', e);
        return 0;
    }
}
/**
 * 计算当前选中日期及范围内日期的位置
 * @param calendarData
 * @param selectedDate
 */
export function computedSelectedSectionIndexes(calendarData, selectedDate) {
    if (!selectedDate || !commonUtil.isValidArray(selectedDate)) {
        return null;
    }
    const defaultDate = selectedDate ? selectedDate[0] : '';
    const dateInfo = parseDateInfo(defaultDate);
    // 找到选中的月份
    const sectionIndex = calendarData.findIndex((item) => item.year === dateInfo.year && item.month === dateInfo.month);
    return computeSelectedIndexes(calendarData, sectionIndex, dateInfo);
}
/**
 * 根据选中日期计算偏移位置下标
 * @param calendarData
 * @param sectionIndex
 * @param dateInfo
 */
export function computeSelectedIndexes(calendarData, sectionIndex, dateInfo) {
    const result = {};
    for (let i = 0; i <= sectionIndex; i++) {
        const { weekList } = calendarData[i];
        if (i !== sectionIndex) {
            result[i] = weekList.length;
        }
        else {
            result[i] = findDateIn2DArray(weekList, dateInfo).row;
        }
    }
    return result;
}
/**
 * 计算
 * @param calendarData
 * @param calendarLayout
 * @param appendHeader
 * @param scrollPosition
 * @param needHeader
 */
export function computeCalendarHeight(calendarData, calendarLayout, appendHeader, scrollPosition = 'month', needHeader = true) {
    const monthHeights = [];
    const result = {};
    const len = calendarData.length;
    for (let i = 0; i < len; i++) {
        const { weekList } = calendarData[i];
        result[i] = weekList.length;
        monthHeights[i] = computeMonthViewHeight(calendarLayout, weekList.length, needHeader);
    }
    const height = computedInitScrollPosition(result, calendarLayout, appendHeader, scrollPosition);
    return {
        height,
        monthHeights
    };
}
/**
 * 计算每个月份的高度
 * @param calendarLayout
 * @param lineLen
 * @param needHeader
 */
export function computeMonthViewHeight(calendarLayout, lineLen, needHeader) {
    const { monthHeader, dayRow, dayItem } = calendarLayout;
    const { marginBottom, marginTop, height } = monthHeader;
    const extraHeight = marginBottom + marginTop + (needHeader ? height : 0);
    return lineLen * (dayItem.height + dayRow.bottom) + extraHeight;
}
/**
 * 从月份二位列表中找到选中的日期的位置
 * @param matrix
 * @param target
 */
export function findDateIn2DArray(matrix, target) {
    try {
        if (!matrix || matrix.length === 0 || matrix[0].length === 0) {
            return { row: 0, column: 0 };
        }
        const rows = matrix.length;
        const columns = matrix[0].length;
        let row = 0;
        let column = columns - 1;
        while (row < rows && column >= 0) {
            const lastDate = matrix[row][column].date;
            // 只有日期存在时，在比较
            if (lastDate && lastDate === target.date) {
                return { row, column };
            }
            else if (!lastDate || lastDate > target.date) {
                // 如果日期不存在或当前日期大于目标日期时，继续在同一行寻找
                column--;
            }
            else {
                row++;
            }
        }
        return { row: 0, column: 0 };
    }
    catch (e) {
        warningLog('findDateIn2DArray error:', e);
        return { row: 0, column: 0 };
    }
}
/* ------- 计算滚动距离逻辑 end ------- */
/**
 * 计算要渲染的下标
 * @param position
 * @param initNum
 */
export function computeInitNumToRender(position, initNum) {
    try {
        const firstSelectIndex = commonUtil.isValidObject(position)
            ? // @ts-ignore
                Math.max(...Object.keys(position))
            : 0;
        // 除去选中的模块外要渲染的数量
        const appendLen = initNum - 1;
        // 上下平分
        const halfLen = appendLen / 2;
        // 前面添加的数量 - 向下取整
        const beforeLenInt = Math.floor(halfLen);
        // 前面添加的真正数量 - 如果当前选中的月份下标大于要补齐的长度，则使用计算的补齐长度；否则直接使用当前月份的下标做补齐数量
        const beforeAppendLen = firstSelectIndex > beforeLenInt ? beforeLenInt : firstSelectIndex;
        // 后面添加的真正数量 - 总的补齐的数量减去前面补齐的数量
        const afterAppendLen = appendLen - beforeAppendLen;
        const result = {
            [firstSelectIndex]: true
        };
        // 设置前面要补齐的标识
        for (let i = 1; i <= beforeAppendLen; i++) {
            result[firstSelectIndex - i] = true;
        }
        // 设置后面要补齐的标识
        for (let j = 1; j <= afterAppendLen; j++) {
            result[firstSelectIndex + j] = true;
        }
        return result;
    }
    catch (e) {
        warningLog('computeInitNumToRender error:', e);
        return { 0: true };
    }
}
/**
 * 当前日期是否已经选中
 * @param selectedDate
 * @param data
 */
export function hasSelected(selectedDate, data) {
    return selectedDate.find((item) => item.key === data.key);
}
/* ------- 浮层日历相关工具方法 start ------- */
/**
 * 计算固定元素的下标
 * @param hasFakeText
 * @param minDate
 * @param maxDate
 */
export function computedFixedIndexes(hasFakeText, minDate, maxDate) {
    // 这里加1，要算是本月
    const months = dateUtil.monthDiff(minDate, maxDate) + 1;
    const indexes = [];
    let start = hasFakeText ? 1 : 0;
    const end = hasFakeText ? months : months - 1;
    for (let i = start; i <= end; i++) {
        indexes.push(start);
        start += 2;
    }
    return indexes;
}
/**
 * 计算阻尼系数
 * @param decelerationRate
 */
export function processDecelerationRate(decelerationRate) {
    if (decelerationRate === 'normal') {
        return Platform.select({
            ios: 0.998,
            android: 0.985
        });
    }
    else if (decelerationRate === 'fast') {
        return Platform.select({
            ios: 0.99,
            android: 0.7
        });
    }
    return decelerationRate;
}
/**
 * 外部dataSource更新时，需要更新用户选中的数据的价格信息
 * @param dataSource
 * @param selectedDate
 */
export function updateUserSelectedDate(dataSource, selectedDate) {
    if (commonUtil.isValidArray(selectedDate)) {
        // 遍历已选日期，使用新的数据覆盖旧的；新数据为空也需要覆盖就内容
        selectedDate.forEach((item) => {
            const { value } = item;
            const { footer, header } = dataSource[value] || {};
            if (item.footer) {
                item.footer = footer === null || footer === void 0 ? void 0 : footer.text;
            }
            if (item.header) {
                item.header = header === null || header === void 0 ? void 0 : header.text;
            }
        });
    }
    return selectedDate;
}
/* ------- 浮层日历相关工具方法 end ------- */
/* ------- 底部栏相关工具方法 start ------- */
export function computeBottomShowData(selectedPayload, userSelectedDate = []) {
    if (!commonUtil.isValidArray(selectedPayload)) {
        return [];
    }
    const priceReg = /¥[0-9]+/;
    // @ts-ignore
    return selectedPayload.map((item, index) => {
        const { text } = item.header || {};
        const { header, footer = '', year, month, date, value = '', week } = userSelectedDate[index] || {};
        const price = priceReg.test(footer)
            ? footer.slice(1)
            : footer;
        return {
            text: text || header,
            price,
            year,
            month,
            date,
            value,
            week,
            showDate: value.slice(5) || BOTTOM_OTHER_INFO_TEXT
        };
    });
}
/**
 * 计算底部展示
 * @param showData
 * @param otherInfo
 * @param mode
 */
export function computeBottomPriceAndDays(showData, otherInfo, mode) {
    var _a, _b, _c, _d, _e, _f, _g;
    try {
        const { type, desc = '' } = otherInfo;
        const showTextArr = desc.split('%');
        // 展示数据为空时，直接返回默认值
        if (!commonUtil.isValidArray(showData)) {
            return { showTextArr, canClick: false };
        }
        const selectedItem = showData.filter((item) => item.showDate && item.showDate !== BOTTOM_OTHER_INFO_TEXT);
        const len = showData.length - 1;
        // 范围和多选时，选够2个日期才可以点击
        const enableNum = mode === CALENDAR_TYPE.RANGE || mode === CALENDAR_TYPE.MULTI
            ? 2
            : showData.length;
        // 是否全部已选择
        const canClick = selectedItem.length >= enableNum;
        if (canClick) {
            if (type === 'interval') {
                const distanceDay = dateUtil.distanceTime((_a = showData[0]) === null || _a === void 0 ? void 0 : _a.value, (_b = showData[len]) === null || _b === void 0 ? void 0 : _b.value);
                showTextArr[1] = distanceDay.toString();
            }
            else if (type === 'price') {
                // 为空时，默认为0
                const firstPrice = ((_c = showData[0]) === null || _c === void 0 ? void 0 : _c.price) ? parseInt((_d = showData[0]) === null || _d === void 0 ? void 0 : _d.price, 10)
                    : 0;
                const secondPrice = ((_e = showData[1]) === null || _e === void 0 ? void 0 : _e.price) ? parseInt((_f = showData[len]) === null || _f === void 0 ? void 0 : _f.price, 10)
                    : 0;
                const totalPrice = firstPrice + secondPrice;
                // 如果不是一个正常的价格，则展示默认文案
                showTextArr[1] =
                    isNaN(totalPrice) || totalPrice <= 0
                        ? showTextArr[1]
                        : (_g = showTextArr[1]) === null || _g === void 0 ? void 0 : _g.replace(/-+/, totalPrice.toString());
            }
        }
        return { showTextArr, canClick };
    }
    catch (e) {
        warningLog('computeBottomPriceAndDays error:', e);
        return { showTextArr: [], canClick: false };
    }
}
/**
 * 转换返回数据
 * @param showData
 */
export function convertCalendarData(showData = []) {
    return showData.map((item, index) => ({
        value: item.value,
        week: item.week,
        footer: item.price,
        header: item.text,
        year: item.year,
        month: item.month,
        date: item.date,
        selectedIndex: index
    }));
}
/* ------- 底部栏相关工具方法 end ------- */
/* ------- FlatList计算逻辑 start ------- */
/**
 * 计算FlatList固定头部下标
 * @param len
 */
export function computeFlatStickyIndices(len) {
    const indices = [];
    for (let i = 0; i < len; i++) {
        indices.push(i * 2);
    }
    return indices;
}
/**
 * 计算FlatList渲染数据
 * @param calendarData
 */
export function computeFlatRenderData(calendarData) {
    const result = [];
    calendarData.forEach((item) => {
        const headerData = {
            index: item.index,
            title: item.title,
            monthKey: item.monthKey,
            year: item.year,
            month: item.year,
            isHeader: true
        };
        result.push(headerData);
        result.push(item);
    });
    return result;
}
/* ------- FlatList计算逻辑 end ------- */
/**
 * 根据设备分级获取渲染参数
 */
export function getDeviceProps(initialNumToRender, useGesture, gestureType) {
    if (deviceUtil.isFastMachine()) {
        /* 高端机
         * ios：初始渲染5个月
         * adr: 初始渲染3个月
         */
        const defaultNum = initialNumToRender || (isIOS ? 5 : 3);
        // 使用native手势浮层时，月份头部+月份内容算作一个渲染块；
        // 不使用手势浮层或使用RN手势浮层时，月份头部和月份内容各自算作一个渲染块，所以需要乘以2
        const initNum = gestureType !== 1 ? defaultNum * 2 : defaultNum;
        return {
            initialNumToRender: initNum,
            useGesture
        };
    }
    /* 低端机
     *  不使用手势浮层
     *  不使用吸顶功能
     *  初始渲染2个月
     */
    return {
        initialNumToRender: initialNumToRender || 4,
        useGesture: false,
        gestureType: 0,
        stickyHeader: false
    };
}
/**
 * 校验当前日期是否可选
 * 优先级： allowDates > denyDates > denyWeeks > startTime & endTime
 * @param param
 */
export function dayDateCanSelect(param) {
    const { value, week, startTime, endTime, allowDates, denyDates, denyWeeks } = param;
    const strVal = dateUtil.dateFormat(value);
    // 只有allowDates时，直接校验；否则逐次校验
    if (commonUtil.isValidArray(allowDates) &&
        !commonUtil.isValidArray(denyWeeks) &&
        !commonUtil.isValidArray(denyDates)) {
        return allowDates.includes(strVal);
    }
    // 如果当前日期在可选日期集合中
    const isInAllowDates = commonUtil.isValidArray(allowDates) && allowDates.includes(strVal);
    // 如果存在，则直接返回 - 可选；不存在继续向下检查
    if (isInAllowDates) {
        return true;
    }
    // 检查当前日期是否在不可选日期集合
    const isInDenyDates = commonUtil.isValidArray(denyDates) && denyDates.includes(strVal);
    // 如果存在，则直接返回 - 不可选；不存在继续向下检查
    if (isInDenyDates) {
        return false;
    }
    // 检查当前日期是否在不可选周次集合
    const isInDenyWeeks = commonUtil.isValidArray(denyWeeks) && denyWeeks.includes(week);
    // 如果存在，则直接返回 - 不可选；不存在继续向下检查
    if (isInDenyWeeks) {
        return false;
    }
    // 如果当前日期在可选日期范围内，则表示可选
    return dateUtil.isWithinDayRange(value, { start: startTime, end: endTime });
}
/**
 * 当开始/结束可选日期、可选/不可选属性变化时，需要重新计算state
 * @param nextProps
 * @param prevState
 */
export function computeDerivedStateData(nextProps, prevState) {
    const { startTime: sStartTime, endTime: sEndTime, allowDates: sAllowDates = [], denyDates: sDenyDates = [] } = prevState || {};
    // 范围日期 - 使用校验后的进行对比
    const { startTime, endTime, allowDates = [], denyDates = [] } = validAndAdjustProps(nextProps, false);
    const prevData = convertDerivedData(sStartTime, sEndTime, sAllowDates, sDenyDates);
    const nextData = convertDerivedData(startTime, endTime, allowDates, denyDates);
    // 如果数据变化，则更新
    if (!performanceUtil.fastDeepEqual(prevData, nextData)) {
        return {
            startTime,
            endTime,
            allowDates,
            denyDates
        };
    }
    return null;
}
/**
 * 将日期统一转为字符串方便比较
 * @param startTime
 * @param endTime
 * @param allowDates
 * @param denyDates
 */
function convertDerivedData(startTime, endTime, allowDates, denyDates) {
    return {
        startTime: startTime ? dateUtil.dateFormat(startTime) : '',
        endTime: endTime ? dateUtil.dateFormat(endTime) : '',
        allowDates: commonUtil.isValidArray(allowDates)
            ? allowDates.map((item) => dateUtil.dateFormat(item))
            : [],
        denyDates: commonUtil.isValidArray(denyDates)
            ? denyDates.map((item) => dateUtil.dateFormat(item))
            : []
    };
}
